import { useMemo, useState } from 'react';
import styles from './styles';
import { Box, Button, DialogActions, Grid, IconButton, MenuItem, Select, TextField, Typography } from '@mui/material';
import { CancelPresentationRounded } from '@mui/icons-material';
import PropTypes from 'prop-types';
import ModalDialog from '../ModalDialog';
import { useDispatch } from 'react-redux';
import { enqueueSnackbar } from '../../../store/actions/snackbar';

const MDTVDInterpolationDialog = ({
  onCloseClick,
  isOpen,
  data
}) => {
  const classes = styles();
  const dispatch = useDispatch();
  const [interpolateBy, setInterpolateBy] = useState('TVD');
  const [inputValue, setInputValue] = useState('');
  const [result, setResult] = useState(null);

  const config = useMemo(() => {
    const isTVD = interpolateBy === 'TVD';
    return {
      inputKey: isTVD ? 'md' : 'tvd',
      resultKey: isTVD ? 'tvd' : 'md',
    };
  }, [interpolateBy]);

  const normalizedData = useMemo(() => {
    return [...data.data]
      .map((row) => ({
        ...row,
        [config.inputKey]: Number(row[config.inputKey]),
        [config.resultKey]: Number(
          row[config.resultKey]
        ),
      }))
      .sort(
        (a, b) =>
          a[config.inputKey] - b[config.inputKey]
      );
  }, [data.data, config]);

  const range = useMemo(() => {
    if (!normalizedData.length) {
      return {
        min: 0,
        max: 0,
      };
    }
    return {
      min: normalizedData[0][config.inputKey],
      max: normalizedData[normalizedData.length - 1][config.inputKey],
    };
  }, [normalizedData, config]);

  const resetState = () => {
    setResult(null);
  };

  const handleSelectionChange = (value) => {
    setInterpolateBy(value);
    setInputValue('');
    resetState();
  };

  const handleInputChange = (value) => {
    setInputValue(value);
    resetState();
  };

  const interpolateLinear = (x, x1, x2, y1, y2) => {
    return y1 + ((x - x1) * (y2 - y1)) / (x2 - x1);
  };

  const showError = (message) => {
    dispatch(enqueueSnackbar(message, 'error'));
  };

  const handleInterpolate = () => {
    resetState();
    const value = Number(inputValue);

    if (!inputValue) {
      showError('Please enter a value');
      return;
    }

    if (Number.isNaN(value)) {
      showError('Invalid number');
      return;
    }

    if (value < range.min || value > range.max) {
      showError(`Value must be between ${range.min}${data?.units?.[config.inputKey]?.label} and ${range.max}${data?.units?.[config.inputKey]?.label}`);
      return;
    }

    const exact = normalizedData.find(
      (row) => row[config.inputKey] === value
    );

    if (exact) {
      setResult(
        exact[config.resultKey].toFixed(data?.units?.[config?.resultKey]?.decimals)
      );
      return;
    }

    const upperIndex = normalizedData.findIndex(
      (row) => row[config.inputKey] > value
    );

    if (upperIndex <= 0 || upperIndex === -1) {
      showError('Interpolation failed');
      return;
    }

    const lower = normalizedData[upperIndex - 1];
    const upper = normalizedData[upperIndex];

    const interpolated = interpolateLinear(
      value,
      lower[config.inputKey],
      upper[config.inputKey],
      lower[config.resultKey],
      upper[config.resultKey]
    );

    setResult(interpolated.toFixed(data?.units?.[config?.resultKey]?.decimals));
  };

  const closeHandler = () => {
    onCloseClick();
  };

  const getModalTitle = () => {
    return (
            <Grid container alignItems="center" justifyContent="space-between">
                <Grid item>
                    MD / TVD Interpolation
                </Grid>
                <Grid item>
                    <IconButton
                        onClick={closeHandler}
                    >
                        <CancelPresentationRounded fontSize='large' />
                    </IconButton>
                </Grid>
            </Grid>
    );
  };

  return (
      <ModalDialog
          title={getModalTitle()}
          isOpen={isOpen}
          onClose={closeHandler}
          dialogClass={classes.dialogModal}
      >
          <Grid container p={3}>
              <Box className={classes.inputContainer} sx={{ gap: 2 }}>
                  <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3
                      }}
                  >
                      <Typography
                          className={classes.inputLabel}
                      >
                          Interpolate
                      </Typography>

                      <Select
                          size="small"
                          value={interpolateBy}
                          onChange={(e) =>
                            handleSelectionChange(
                              e.target.value
                            )
                          }
                          className={classes.selectionText}
                      >
                          <MenuItem value="MD">MD</MenuItem>
                          <MenuItem value="TVD">TVD</MenuItem>
                      </Select>
                  </Box>

                  <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3,
                        mb: 2
                      }}
                  >
                      <Typography
                          className={classes.inputLabel}
                      >
                          {config.inputKey.toUpperCase()}{' '} Value ({data?.units?.[config.inputKey]?.label})
                      </Typography>

                      <TextField
                          size="large"
                          value={inputValue}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^-?\d*\.?\d*$/.test(value)) {
                              handleInputChange(value);
                            }
                          }
                          }
                          helperText={`Range: ${range.min}${data?.units?.[config.inputKey]?.label} - ${range.max}${data?.units?.[config.inputKey]?.label}`}
                          className={classes.selectionText}
                      />
                  </Box>

                  {!result
                    ? (
                          <Grid
                              container
                              item
                              justifyContent="center"
                              marginBottom="1rem"
                              marginTop="1rem"
                          >
                              <DialogActions>
                                  <Button
                                      sx={{ backgroundColor: '#444', width: '200px' }}
                                      onClick={handleInterpolate}
                                  >
                                      Calculate
                                  </Button>
                              </DialogActions>
                          </Grid>
                      )
                    : (
                          <Typography
                              sx={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                mt: 2
                              }}
                          >
                              Interpolated{' '}
                              {config.resultKey.toUpperCase()} ={' '}
                              {result} {data?.units?.[config.resultKey]?.label}
                          </Typography>
                      )}

              </Box>
          </Grid>
      </ModalDialog>
  );
};
MDTVDInterpolationDialog.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  data: PropTypes.any,
};

export default MDTVDInterpolationDialog;
